import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchBlogPosts } from "../lib/api";

const Blog = () => {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchBlogPosts(lang)
      .then((data) => setPosts(data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [lang]);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <div data-testid="blog-page">
      <section className="pt-20 pb-12 md:pt-28 md:pb-16">
        <div className="container-rotam">
          <p className="overline">{t.blog.overline}</p>
          <h1 className="mt-4 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            {t.blog.title}
          </h1>
          <p className="mt-6 text-rotam-muted text-base md:text-lg max-w-2xl leading-relaxed">
            {t.blog.description}
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-rotam">
          {loading ? (
            <div data-testid="blog-loading" className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="bg-white border border-rotam-border rounded-sm overflow-hidden">
                  <div className="h-56 bg-rotam-border/40 animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-rotam-border/40 animate-pulse rounded" />
                    <div className="h-4 w-3/4 bg-rotam-border/40 animate-pulse rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div data-testid="blog-empty" className="text-center py-20 text-rotam-muted">
              —
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p) => (
                <Link
                  to={`/blog/${p.slug}`}
                  key={p.slug}
                  data-testid={`blog-card-${p.slug}`}
                  className="group bg-white border border-rotam-border rounded-sm overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="flex items-center gap-4 text-[11px] tracking-[0.15em] uppercase text-rotam-muted">
                      <span className="flex items-center gap-1.5">
                        <Calendar size={12} /> {formatDate(p.published_at)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock size={12} /> {p.read_minutes} {t.blog.minRead}
                      </span>
                    </div>
                    <h3 className="mt-4 font-heading text-xl font-medium text-rotam-ink leading-snug group-hover:text-rotam-primary transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-rotam-muted leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm text-rotam-primary font-medium tracking-wide">
                      {t.blog.readMore} <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
